import axios from "axios";
import { getStoreCartLineUnitPrice } from "./storeUtils";

function fbq(...args) {
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq(...args);
  }
}

function makeEventId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function hasFbpCookie() {
  return typeof document !== "undefined" && /(?:^|;\s*)_fbp=/.test(document.cookie);
}

// The Meta Pixel library loads async and sets the _fbp cookie shortly after
// init; without it Meta rejects the server-side event as unmatchable. Wait
// briefly for it on the very first event of a fresh page load.
function waitForFbpCookie(maxWaitMs = 1000, intervalMs = 50) {
  return new Promise((resolve) => {
    if (hasFbpCookie()) {
      resolve();
      return;
    }
    const start = Date.now();
    const timer = setInterval(() => {
      if (hasFbpCookie() || Date.now() - start >= maxWaitMs) {
        clearInterval(timer);
        resolve();
      }
    }, intervalMs);
  });
}

function sendToServer(eventName, eventId, data, identity) {
  axios
    .post("/meta/capi-event", {
      eventName,
      eventId,
      eventSourceUrl: typeof window !== "undefined" ? window.location.href : undefined,
      contentIds: data.content_ids,
      contentType: data.content_type,
      currency: data.currency,
      value: data.value,
      numItems: data.num_items,
      email: identity?.email || undefined,
      phone: identity?.phone || undefined,
      externalId: identity?.externalId || undefined,
    })
    .catch(() => {});
}

async function track(eventName, data, identity, explicitEventId) {
  const eventId = explicitEventId || makeEventId();
  fbq("track", eventName, data, { eventID: eventId });
  await waitForFbpCookie();
  sendToServer(eventName, eventId, data, identity);
}

// Products without a price are skipped by the catalog sync, so their events
// can never match a catalog item — sending them only lowers the match rate.
function isInCatalog(product) {
  return Boolean(product?._id) && Number(product.price) > 0;
}

// Meta rejects value <= 0 ("value must be a number greater than 0" diagnostic),
// so send value/currency only when we actually have a positive amount.
function withValue(data, value) {
  const amount = Math.round(Number(value) * 100) / 100;
  if (Number.isFinite(amount) && amount > 0) {
    return { ...data, value: amount, currency: "SAR" };
  }
  return data;
}

// Best positive unit price for a catalog product: the configured/discounted
// price when it's a real amount, else the list price. isInCatalog guarantees
// the list price is > 0, so events for catalog products always carry a value
// (Meta flags value-less events as a data quality issue).
function effectiveUnitPrice(product, preferredPrice) {
  const preferred = Number(preferredPrice);
  if (Number.isFinite(preferred) && preferred > 0) return preferred;
  const discounted = product.discount_percentage > 0
    ? product.price * (1 - product.discount_percentage / 100)
    : product.price;
  if (Number.isFinite(discounted) && discounted > 0) return discounted;
  return Number(product.price);
}

// `identity` (email/phone/externalId of a logged-in user) is optional and
// improves Meta's event match quality score when available.
export function pixelViewContent(product, identity) {
  if (!isInCatalog(product)) return;
  track(
    "ViewContent",
    withValue(
      {
        content_ids: [product._id],
        content_type: "product",
        content_name: product.name,
      },
      effectiveUnitPrice(product)
    ),
    identity
  );
}

// `unitPrice` overrides the catalog price for options-priced lines (curtain
// size configurations), keeping AddToCart consistent with the cart total.
export function pixelAddToCart(product, quantity = 1, identity, unitPrice) {
  if (!isInCatalog(product)) return;
  track(
    "AddToCart",
    withValue(
      {
        content_ids: [product._id],
        content_type: "product",
        content_name: product.name,
        num_items: quantity,
      },
      effectiveUnitPrice(product, unitPrice) * quantity
    ),
    identity
  );
}

export function pixelInitiateCheckout(cart, identity) {
  if (!cart?.length) return;
  // Same pricing logic as the cart total (handles price_override for
  // custom-size lines), unlike the raw item.price fields.
  const cartTotal = cart.reduce(
    (sum, item) => sum + getStoreCartLineUnitPrice(item) * (item.quantity || 1),
    0
  );
  // If a malformed line zeroes the cart total, the list prices still give a
  // usable value — better than sending the event without one.
  const listTotal = cart.reduce(
    (sum, item) =>
      sum + (isInCatalog(item) ? effectiveUnitPrice(item) : 0) * (item.quantity || 1),
    0
  );
  track(
    "InitiateCheckout",
    withValue(
      {
        content_ids: cart.filter(isInCatalog).map((item) => item._id),
        content_type: "product",
        num_items: cart.reduce((sum, item) => sum + (item.quantity || 1), 0),
      },
      cartTotal > 0 ? cartTotal : listTotal
    ),
    identity
  );
}

// Uses the server-confirmed order (authoritative prices/totals) rather than
// client-side cart state, which can be stale or incomplete by checkout time.
// The order's own customer info is always used for matching — it's the most
// reliable identity data available (form-confirmed at the point of purchase).
export function pixelPurchase(order) {
  if (!order?.items?.length) return;
  const value = Number(order.totals?.total);
  if (!Number.isFinite(value) || value <= 0) return;
  track(
    "Purchase",
    {
      content_ids: order.items.map((item) => item.productId),
      content_type: "product",
      num_items: order.items.reduce((sum, item) => sum + (item.quantity || 1), 0),
      value,
      currency: order.totals?.currency || "SAR",
      order_id: order._id,
    },
    {
      email: order.customer?.email,
      phone: order.customer?.phone,
      externalId: order.userId,
    },
    // Same deterministic id as the server-side event so Meta dedupes them.
    `purchase-${order._id}`
  );
}
